import { useTranslation } from 'react-i18next';
import type { Control, FieldValues } from 'react-hook-form';
import {
  Flex,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@kognitos/lattice';
import {
  Bdkv2CredentialType,
  type Bdkv2BookDescriptor,
  type Bdkv2CredentialDescriptor,
} from '@/generated/hooks/bdk/models';
import { getNoAutofillProps } from '@/shared/utils';

interface BDKCredentialType extends Bdkv2CredentialDescriptor {
  type?: Bdkv2CredentialType;
  label?: string;
  name?: string;
}

export interface OAuthClientCredentialsStepProps {
  integration: Bdkv2BookDescriptor;
  authMethodId: string;
  control: Control<FieldValues>;
  fieldPrefix?: string;
  disabled?: boolean;
  readOnly?: boolean;
}

export function OAuthClientCredentialsStep({
  integration,
  authMethodId,
  control,
  fieldPrefix = '',
  disabled = false,
  readOnly = false,
}: OAuthClientCredentialsStepProps) {
  const oauthClientCredentials = integration.authentications?.find(
    auth => auth.oauth?.id === authMethodId,
  );

  const { t: tIntegrations } = useTranslation('integrations');

  // Get credentials and arguments from the OAuth authentication object
  const credentials = (oauthClientCredentials?.oauth?.credentials ||
    []) as BDKCredentialType[];
  const arguments_ = (oauthClientCredentials?.oauth?.arguments ||
    []) as BDKCredentialType[];

  const formFields: BDKCredentialType[] = [...credentials, ...arguments_];

  return (
    <div className="space-y-4">
      {formFields.map(argument => {
        const fieldName = argument.id || '';
        const fullFieldName = fieldPrefix
          ? `${fieldPrefix}.${fieldName}`
          : fieldName;

        return (
          <FormField
            key={fullFieldName}
            control={control}
            name={fullFieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Flex align="center" gap={2}>
                    {argument.label || argument.name}
                    {disabled && (
                      <span className="text-xs text-muted-foreground leading-[inherit]">
                        {tIntegrations(
                          'integrations.connections.copiedFromDraft',
                        )}
                      </span>
                    )}
                  </Flex>
                </FormLabel>
                <FormControl>
                  <Input
                    type={
                      argument.type ===
                      Bdkv2CredentialType.CredentialTypeSensitiveText
                        ? 'password'
                        : 'text'
                    }
                    disabled={disabled}
                    readOnly={!disabled && readOnly}
                    {...field}
                    {...getNoAutofillProps({
                      sensitive:
                        argument.type ===
                        Bdkv2CredentialType.CredentialTypeSensitiveText,
                    })}
                    value={field.value || ''}
                  />
                </FormControl>
                <FormDescription>{argument.description}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      })}
    </div>
  );
}
